import { useMutation, useQueryClient } from "@tanstack/react-query";
import React, { useCallback, useRef, useState } from "react";
import { FaRegCopy } from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import {
  copyAnswersFromPreviousReport,
  deleteReport,
  updateReportState,
} from "../../api/reportsApi";
import { fetchStudy } from "../../api/studyApi";
import { successToast, failureSaveToast } from "../../helpers/successToast";
import { useOutsideAlerter } from "../../hooks/useOutsideAlert";
import AddItemWithExistingPicker, {
  InputType,
} from "../../pages/template/AddItemWithExistingPicker";
import { ReportState, TemplateType } from "../../types/Rows";
import { cn } from "../../lib/utils";
import { Button } from "../ui/button";
import { ConfirmationLetterModal } from "../../pages/confirmationletter/ConfirmationLetter";
import { PdfDesignPicker, usePdfDesign } from "../pdfDesign/PdfDesign";
import { FollowUpLetterModal } from "../../pages/followupletter/FollowUpLetter";
import { LastGeneratedInfo } from "../generatedDocuments/GeneratedDocuments";

const IconDown = () => (
  <svg className="-mr-1 h-4 w-4 text-[var(--sc-muted)]" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
    <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
  </svg>
);

const STATUS_STEPS: { label: string; value: ReportState }[] = [
  { label: "W trakcie",      value: ReportState.INPROGRESS },
  { label: "Do weryfikacji", value: ReportState.TO_VERIFY  },
  { label: "Do poprawy",     value: ReportState.TO_CORRECT },
  { label: "Zaakceptowany",  value: ReportState.ACCEPTED   },
];

const ReportToolbar = ({
  state,
  resortName,
  reportId,
  MyDoc,
  studyId,
  templateType,
}: {
  reportId: string;
  resortName: string;
  state: ReportState;
  MyDoc: any;
  studyId: string;
  templateType: number;
}) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const isStandardReport = templateType !== 1;

  const { mutate: deleteTemplateAction } = useMutation(
    (rid: string) => deleteReport(rid),
    {
      onSuccess: () => {
        navigate("../study/" + studyId, { replace: true });
      },
    }
  );

  const invalidateReport = useCallback(
    () => queryClient.resetQueries([reportId, "Report"]),
    [reportId, queryClient]
  );

  const { mutateAsync: copyFromExisting } = useMutation(
    async ({ reportId, sourceId }: { reportId: string; sourceId: string }) =>
      await copyAnswersFromPreviousReport(reportId, sourceId),
    {
      onSuccess: () => {
        successToast();
        invalidateReport();
        window.location.reload();
      },
    }
  );

  const exportTypes = new Map<string, TemplateType>([
    ["Informacje ogólne", TemplateType.METADATA],
    ["Status", TemplateType.STATUS],
    ["Akcje otwarte i zamknięte", TemplateType.ACTIONS],
    ["Lista znalezisk", TemplateType.ISSUES],
    ["Pacjenci", TemplateType.PARTICIPANTS],
    ["Pytania", TemplateType.QUESTIONS],
    ["Komentarz końcowy", TemplateType.FINALCOMMENT],
    ["Stopka", TemplateType.FOOTER],
    ["Załączniki", TemplateType.ATTACHMENTS],
  ]);

  const defaultExportTypes: TemplateType[] =
    templateType === 1
      ? [
          TemplateType.QUESTIONS,
          TemplateType.FINALCOMMENT,
          TemplateType.FOOTER,
          TemplateType.ATTACHMENTS,
        ]
      : [
          TemplateType.ISSUES,
          TemplateType.STATUS,
          TemplateType.METADATA,
          TemplateType.PARTICIPANTS,
          TemplateType.QUESTIONS,
          TemplateType.FOOTER,
          TemplateType.FINALCOMMENT,
          TemplateType.ACTIONS,
          TemplateType.ATTACHMENTS,
        ];

  const [isExportOpen, setIsExportOpen] = useState<boolean>(false);
  const [pdfDesign, choosePdfDesign] = usePdfDesign();
  const [selectedExportTypes, setSelectedExportTypes] = useState<TemplateType[]>(defaultExportTypes);
  const [showCLModal, setShowCLModal] = useState(false);
  const [showFULModal, setShowFULModal] = useState(false);

  useOutsideAlerter(wrapperRef, () => setIsExportOpen(false));

  const handleTypeToggle = (value: TemplateType) => {
    setSelectedExportTypes((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  return (
    <div className="flex flex-col flex-shrink-0">

      {/* Status flow */}
      <div className="px-5 pt-5 pb-0 border-b border-[var(--sc-border)]">
        <div className="text-[10px] font-medium uppercase tracking-[0.08em] text-[var(--sc-muted)] mb-3">
          Status raportu
        </div>
        <div className="flex gap-0">
          <ReportStateItems state={state} reportId={reportId} />
        </div>
        <div className="h-3" />
      </div>

      {/* Action buttons */}
      <div className="px-4 py-3 flex flex-col gap-1.5">
        {/* PDF export */}
        <div ref={wrapperRef} className="w-full">
          <button
            type="button"
            onClick={() => setIsExportOpen(!isExportOpen)}
            className="w-full flex items-center justify-center gap-1.5 bg-[var(--sc-amber)] text-[var(--sc-text)] border-none rounded-[8px] px-4 py-2 text-[12px] font-semibold cursor-pointer hover:bg-[#e09920] transition-colors"
          >
            ↓ Eksportuj PDF
            <IconDown />
          </button>
          {isExportOpen && (
            <div className="absolute mt-1.5 w-52 bg-[var(--sc-surface)] border border-[var(--sc-border)] rounded-[10px] shadow-[0_8px_24px_rgba(0,0,0,0.12)] z-50 overflow-hidden">
              {isStandardReport && (
                <div className="py-1.5 px-2 border-b border-[var(--sc-border)]">
                  <PdfDesignPicker value={pdfDesign} onChange={choosePdfDesign} name="pdf-design-report" />
                </div>
              )}
              <div className="py-1 px-2">
                {[...exportTypes.keys()].map((key) => (
                  <label key={key} className="flex items-center gap-2 px-2 py-1.5 text-[11px] text-[var(--sc-muted)] cursor-pointer rounded-[6px] hover:bg-[var(--sc-bg)]">
                    <input
                      type="checkbox"
                      checked={selectedExportTypes.includes(exportTypes.get(key)!)}
                      onChange={() => handleTypeToggle(exportTypes.get(key)!)}
                      className="accent-[#F4A832]"
                    />
                    {key}
                  </label>
                ))}
              </div>
              <div className="px-3 py-2 border-t border-[var(--sc-border)]">
                <MyDoc isFul={false} types={selectedExportTypes} design={isStandardReport ? pdfDesign : "v1"} />
              </div>
            </div>
          )}
        </div>

        {/* Letter buttons — only for standard (non-CM) reports */}
        {isStandardReport && (
          <>
            <ConfirmationLetterModal
              reportId={reportId}
              open={showCLModal}
              onClose={() => setShowCLModal(false)}
            />
            <FollowUpLetterModal
              reportId={reportId}
              open={showFULModal}
              onClose={() => setShowFULModal(false)}
            />
            <button
              onClick={() => setShowCLModal(true)}
              className="w-full flex items-center justify-center gap-1.5 bg-[#EEF4FF] text-[#3570C4] border border-[#C5D8F5] rounded-[8px] px-3 py-2 text-[12px] font-semibold cursor-pointer hover:bg-[#DDEAFF] transition-colors"
            >
              📄 Confirmation Letter
            </button>
            <LastGeneratedInfo reportId={reportId} type="CL" />
            <button
              onClick={() => setShowFULModal(true)}
              className="w-full flex items-center justify-center gap-1.5 bg-[#EEF4FF] text-[#3570C4] border border-[#C5D8F5] rounded-[8px] px-3 py-2 text-[12px] font-semibold cursor-pointer hover:bg-[#DDEAFF] transition-colors"
            >
              📄 Follow Up Letter
            </button>
            <LastGeneratedInfo reportId={reportId} type="FUL" />
          </>
        )}

        {/* Copy from existing */}
        <AddItemWithExistingPicker
          saveExisting={(sourceId) => copyFromExisting({ reportId: reportId!, sourceId })}
          inputType={InputType.PICKFROMEXISTING}
          saveNew={() => {}}
          items={async () => {
            const studies = await fetchStudy(studyId!);
            return (
              studies?.resorts
                .flatMap((x: any) => ({ reports: x.reports, resortName: x.name }))
                .flatMap((x: any) =>
                  x.reports
                    .map((report: any) => ({ id: report.id, name: x.resortName + " - " + report.name }))
                    .filter((x: any) => x.id !== reportId)
                ) ?? []
            );
          }}
          text="Skopiuj z istniejącego"
          buttonStyle="w-full flex items-center justify-center gap-1.5 bg-transparent text-[var(--sc-muted)] border border-[var(--sc-border)] rounded-[8px] px-3 py-1.5 text-[12px] cursor-pointer hover:border-[#C0C0BE] transition-colors"
        >
          <FaRegCopy className="h-3 w-3" /> Skopiuj z istniejącego
        </AddItemWithExistingPicker>

        {/* Delete */}
        <button
          onClick={() => { if (window.confirm("Czy na pewno chcesz usunąć raport?")) deleteTemplateAction(reportId!); }}
          className="w-full flex items-center justify-center bg-transparent text-[#C03030] border border-[#F0D0D0] rounded-[8px] px-3 py-1.5 text-[12px] cursor-pointer hover:bg-[#FEE8E8] transition-colors"
        >
          Usuń raport
        </button>
      </div>
    </div>
  );
};

function ReportStateItems({ state, reportId }: { reportId: string; state: ReportState }) {
  const [reportState, setReportState] = useState<ReportState>(state);

  const { mutate } = useMutation(
    async (val: ReportState) => await updateReportState(reportId, val),
    {
      onSuccess: () => successToast(),
      onError: () => {
        failureSaveToast("statusu raportu");
        setReportState(state);
      },
    }
  );

  const changeState = (s: ReportState) => {
    setReportState(s);
    mutate(s);
  };

  const currentIndex = STATUS_STEPS.findIndex((s) => s.value === reportState);

  return (
    <>
      {STATUS_STEPS.map((step, i) => {
        const isDone = i < currentIndex;
        const isCurrent = i === currentIndex;
        return (
          <div
            key={step.value}
            onClick={() => changeState(step.value)}
            className={cn(
              "flex-1 text-center py-[5px] px-0.5 text-[9px] cursor-pointer border-t-[2.5px] relative transition-colors leading-[1.3] select-none",
              isDone
                ? "text-[#1A8055] border-[#2BAE72]"
                : isCurrent
                ? "text-[var(--sc-text)] border-[var(--sc-text)] font-semibold"
                : "text-[var(--sc-muted)] border-[var(--sc-border)] hover:border-[#C0C0BE]"
            )}
          >
            {isCurrent && (
              <span className="absolute -top-[6px] left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[var(--sc-text)]" />
            )}
            {step.label}
          </div>
        );
      })}
    </>
  );
}

export default ReportToolbar;
